import type {NormalizedCacheObject} from '@apollo/client';
import {
    type ApolloClientProxy,
    type BaseHttpCodes,
    EntityBaseGqlService,
    type GetDownloadLinkRequestPayload,
    type IDownloadByLinkService,
    type ServiceResponsePayload,
} from '@igp/shared/api';
import type {
    AdjustAgentDebtInput,
    DebtTransferLog,
    Mutation,
    MutationAdjustAgentDebtArgs,
    Query,
    QueryGetDebtTransferLogsArgs,
    Subscription,
} from '@igp/shared/contract/pam';
import type {IMessageLocalizer} from '@igp/shared/localization';
import {map} from '@igp/shared/otel';
import type {EntityFetchRequestPayload, EntityFetchServiceResponsePayload} from '@igp/shared/redux/entity';
import {inject, injectable} from 'inversify';
import type {Observable} from 'rxjs';

import {ServiceTypes} from '@ioc-container';

import {lastUpdatedItemId} from './constants';
import {DebtTransferLogRequestBuilder, type IDebtTransferLogRequestBuilder} from './requestBuilder';
import type {
    DebtTransferLogDownloadFields,
    DebtTransferLogEntityType,
    DebtTransferLogExtended,
    DebtTransferLogFilterKeys,
    DebtTransferLogQueryFields,
} from './types';

export interface IDebtTransferLogService
    extends IDownloadByLinkService<DebtTransferLogDownloadFields>,
        EntityBaseGqlService<
            DebtTransferLogEntityType,
            Query,
            QueryGetDebtTransferLogsArgs,
            DebtTransferLogQueryFields,
            Mutation,
            Subscription,
            BaseHttpCodes,
            DebtTransferLogFilterKeys
        > {
    adjustAgentDebt(payload: AdjustAgentDebtInput): Observable<ServiceResponsePayload<AdjustAgentDebtInput, DebtTransferLog>>;
}

@injectable()
export class DebtTransferLogService
    extends EntityBaseGqlService<
        DebtTransferLogEntityType,
        Query,
        QueryGetDebtTransferLogsArgs,
        DebtTransferLogQueryFields,
        Mutation,
        Subscription,
        BaseHttpCodes,
        DebtTransferLogFilterKeys
    >
    implements IDebtTransferLogService
{
    protected readonly _requestBuilder: IDebtTransferLogRequestBuilder;

    constructor(
        @inject(ServiceTypes.ApolloClientIGP) client: ApolloClientProxy<NormalizedCacheObject>,
        @inject(ServiceTypes.MessageLocalizer) messageLocalizer: IMessageLocalizer
    ) {
        const builder = new DebtTransferLogRequestBuilder();
        super(client, builder, messageLocalizer);
        this._requestBuilder = builder;
    }
    adjustAgentDebt(payload: AdjustAgentDebtInput): Observable<ServiceResponsePayload<AdjustAgentDebtInput, DebtTransferLog>> {
        const mutation = this._requestBuilder.buildAdjustAgentDebtMutation();
        return this._service
            .mutate<MutationAdjustAgentDebtArgs>(mutation, {adjustment_payload: payload})
            .pipe(map(r => ({...r, requestPayload: payload, responsePayload: r.responsePayload?.adjustAgentDebt})));
    }

    get(
        requestPayload: EntityFetchRequestPayload<DebtTransferLogEntityType, DebtTransferLogQueryFields>
    ): Observable<EntityFetchServiceResponsePayload<DebtTransferLogEntityType, DebtTransferLogExtended>> {
        return this.fetchData(requestPayload).pipe(
            map(response => {
                const query = response.responsePayload?.getDebtTransferLogs;
                const items = query?.items ?? [];
                const totalCount = query?.total_count ?? 0;
                const lastUpdatedItem = {
                    is_general: true,
                    last_updated: query?.last_updated,
                    log_id: lastUpdatedItemId,
                } as DebtTransferLogExtended;
                return {
                    ...response,
                    requestPayload,
                    responsePayload: {
                        items: [...items, lastUpdatedItem],
                        total: totalCount,
                    },
                };
            })
        );
    }

    getDownloadLink(
        requestPayload: GetDownloadLinkRequestPayload<DebtTransferLogDownloadFields>
    ): Observable<ServiceResponsePayload<GetDownloadLinkRequestPayload<DebtTransferLogDownloadFields>, string>> {
        const request = this._requestBuilder.buildDownloadRequest(requestPayload);
        return this._service.query(request.query, request.variables).pipe(
            map(response => ({
                ...response,
                requestPayload,
                responsePayload: response?.responsePayload?.exportDebtTransferLogs?.url,
            }))
        );
    }

    protected getErrorSeverity(_code: BaseHttpCodes): number {
        return 0;
    }
}
